/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import CurrentPrice from './current-price'
import ListPrice from './list-price'
import {FormattedMessage} from 'react-intl'

/**
 * @param priceData - price info extracted from a product
 * // If a product is a set,
 *      on PLP, Show From X where X is the lowest of its children
 *      on PDP, Show From X where X is the lowest of its children and
 *          the set children will have it own price as From X (cross) Y
 * // if a product is a master
 *      on PLP and PDP, show From X (cross) Y , the X and Y are
 *          current and list price of variant that has the lowest price (including promotionalPrice)
 * // if a standard/bundle
 *      show exact price on PLP and PDP as X (cross) Y
 * @param currency - currency
 * @param quantity - quantity to take into the account for price display
 * @param currentPriceProps - extra props to be passing to CurrentPrice component
 * @param listPriceProps - extra props to be passing to ListPrice component
 * @param labelForA11y - label to be used for a11y
 */
const DisplayPrice = ({
    labelForA11y,
    priceData,
    currency,
    quantity = 1,
    currentPriceProps = {},
    listPriceProps = {},
    isDisplayPricePerUnit = false,
    showQuantity = false
}) => {
    const {
        listPrice,
        currentPrice,
        isASet,
        isMaster,
        isOnSale,
        isRange,
        pricePerUnit,
        hasPromotion,
        hasPromotionalPrice,
        hasOnlyApproachingPromotion,
        isFixedPriceModel
    } = priceData
    const renderCurrentPrice = (isRange) => {
        // Show red if on sale OR has a real promotion (not just approaching discount)
        const shouldShowRed =
            !isFixedPriceModel &&
            listPrice &&
            (isOnSale || (hasPromotion && hasPromotionalPrice && !hasOnlyApproachingPromotion))
        const priceProps = shouldShowRed
            ? {...currentPriceProps, color: 'red.700'}
            : currentPriceProps

        return (
            <>
                {showQuantity && (
                    <Text>
                        <FormattedMessage defaultMessage="{quantity} x " values={{quantity}} />
                    </Text>
                )}
                <CurrentPrice
                    labelForA11y={labelForA11y}
                    price={isDisplayPricePerUnit ? pricePerUnit : currentPrice}
                    currency={currency}
                    isRange={isRange}
                    isUnitPrice={isDisplayPricePerUnit}
                    fontSize={{base: 'xs', lg: 'sm'}}
                    {...priceProps}
                />
            </>
        )
    }

    const renderListPrice = (isRange) =>
        !isFixedPriceModel &&
        listPrice &&
        listPrice !== currentPrice && (
            <ListPrice
                labelForA11y={labelForA11y}
                currency={currency}
                price={
                    isDisplayPricePerUnit && !(isOnSale || hasPromotion) ? pricePerUnit : listPrice
                }
                isRange={isRange}
                fontSize={{base: 'xs', lg: 'sm'}}
                {...listPriceProps}
            />
        )

    const renderPriceSet = (isRange) => (
        <Flex alignItems="center" gap={2}>
            {renderCurrentPrice(isRange)}{' '}
            {!isFixedPriceModel && (isOnSale || hasPromotion) && renderListPrice(isRange)}
        </Flex>
    )

    if (isASet) {
        return renderCurrentPrice(true)
    }

    if (isMaster) {
        return renderPriceSet(isRange)
    }

    return <Box>{renderPriceSet(false)}</Box>
}

DisplayPrice.propTypes = {
    priceData: PropTypes.shape({
        currentPrice: PropTypes.number.isRequired,
        isOnSale: PropTypes.bool.isRequired,
        listPrice: PropTypes.number,
        pricePerUnit: PropTypes.number,
        isASet: PropTypes.bool,
        isMaster: PropTypes.bool,
        isRange: PropTypes.bool,
        maxPrice: PropTypes.number,
        tieredPrice: PropTypes.number,
        hasOnlyApproachingPromotion: PropTypes.bool,
        hasPromotion: PropTypes.bool,
        isFixedPriceModel: PropTypes.bool
    }),
    currency: PropTypes.string.isRequired,
    labelForA11y: PropTypes.string,
    currentPriceProps: PropTypes.object,
    listPriceProps: PropTypes.object,
    quantity: PropTypes.number,
    isDisplayPricePerUnit: PropTypes.bool,
    showQuantity: PropTypes.bool
}

export default DisplayPrice
